import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
  Stack,
  CardContent,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import Tooltip from "@mui/material/Tooltip";
import SearchIcon from "@mui/icons-material/Search";
import { useDispatch, useSelector } from "react-redux";
import { useTMutation } from "../../../utils/useTMutation";
import jobRoleImage from "../../../assets/images/unsplash_O453M2Liufs.png";
import {
  setSelectedCard,
  updateSavedJob,
} from "../../../slice/selectedJobCardSlice";
import {
  formatDateAndTime,
  getStatusBgColor,
  getStatusColor,
  getStatusonJobCard,
} from "../../../utils/genericMethod";
import JobCardDetails from "../../../components/Job/JobCardDetails";
import { usePaginatedQuery } from "../../../utils/usePagination";
import { useInView } from "react-intersection-observer";
import Tabs from "../../../components/customTable/Tabs";
import { useTQuery } from "../../../utils/useTQuery";
import Swal from "sweetalert2";
import { FRONTEND_URL } from "../../../appConstants";
import copy from "copy-to-clipboard";

// ✅ Skeleton card shown while filtering/loading
const JobCardSkeleton = () => (
  <Grid item lg={4}>
    <Card
      sx={{
        borderRadius: "8px",
        padding: "8px",
        height: "260px",
        border: "1px solid #ddd",
      }}
    >
      <Box
        sx={{
          background: "#f0f0f0",
          height: "40px",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          background: "#f0f0f0",
          height: "20px",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          background: "#f0f0f0",
          height: "20px",
          width: "60%",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          background: "#f0f0f0",
          height: "20px",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          background: "#f0f0f0",
          height: "20px",
          width: "80%",
          borderRadius: "4px",
          mb: 1,
        }}
      />
      <Box
        sx={{
          background: "#f0f0f0",
          height: "20px",
          width: "50%",
          borderRadius: "4px",
        }}
      />
    </Card>
  </Grid>
);

const ClinicianJobs = () => {
  const dispatch = useDispatch();
  let TempID = sessionStorage.getItem("TempID");
  let HiringType = sessionStorage.getItem("HiringType");
  const { userDetails } = useSelector((state) => state.user);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchValues, setSearchValues] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { selectedCard } = useSelector((state) => state.selectedJob);
  const [showModal, setShowModal] = useState(false);
  const [customFetch, setCustomFetch] = useState(false);
  const jobsTypeTabs = [{ title: "Open Jobs" }, { title: "Assigned Jobs" }];
  const [jobTabactive, setJobTabActive] = useState(
    HiringType ? HiringType : jobsTypeTabs ? jobsTypeTabs[0]?.title : "",
  );
  const [selectedJobRoles, setSelectedJobRoles] = useState([]);
  const [debouncedJobRoles, setDebouncedJobRoles] = useState([]);

  const { ref, inView } = useInView({
    threshold: 0.5,
  });

  useEffect(() => {
    if (HiringType) {
      setJobTabActive(HiringType);
    }
  }, [HiringType]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchValues);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchValues]);

  // ✅ Debounce job role filter - reduced to 300ms for snappier feel
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedJobRoles(selectedJobRoles);
    }, 300);
    return () => clearTimeout(handler);
  }, [selectedJobRoles]);

  useEffect(() => {
    setShowModal(selectedCard?.showModal1);
  }, [selectedCard?.showModal1]);

  const { data: jobRoleList } = useTQuery("jobRoles", "/specialties", "get");
  const jobRoles = useMemo(() => {
    return jobRoleList?.OUTPUT?.length > 0 ? jobRoleList.OUTPUT : [];
  }, [jobRoleList]);

  const { refetch: triggerDocumentNotificationAPI } = useTQuery(
    ["documentNotifications", userDetails?.ID],
    `/job/sendNotificationAfterDocumentExpire?ID=${userDetails?.ID}`,
    "get",
    { enabled: false },
  );

  useEffect(() => {
    if (userDetails?.ID) {
      triggerDocumentNotificationAPI();
    }
  }, [userDetails?.ID]);

  // ✅ Single values useMemo using debouncedJobRoles
  const values = useMemo(
    () => ({
      USER_ID: userDetails?.ID,
      TempID: TempID != null ? TempID : "",
      JOBROLE_ID: userDetails?.JOBROLE_ID,
      USER_TYPE: userDetails?.USER_TYPE,
      HIRING_TYPE: HiringType !== null ? HiringType : jobTabactive,
      searchValues: debouncedSearch,
      selectedJobRoles: debouncedJobRoles, // ✅ debounced roles
      FILTER_VALUE: "",
      FILTER_LOCATION: "",
      JOB_ROLE_ID: "",
      JOB_TYPE: "",
      SELECTED_SPECILITY: "",
      SEARCH_PARMETER_LIST: ["JOB_TYPE"],
    }),
    [
      jobTabactive,
      userDetails?.ID,
      userDetails?.JOBROLE_ID,
      userDetails?.USER_TYPE,
      debouncedSearch,
      debouncedJobRoles, // ✅ debouncedJobRoles in deps
    ],
  );

  const saveJobMutation = useTMutation(
    "/job/saveAndRemoveJob",
    "post",
    (response) => {
      if (response.STATUS) {
        return response;
      }
    },
  );

  // ✅ Set ALL roles selected by default
  useEffect(() => {
    if (jobRoles.length > 0) {
      setSelectedJobRoles(jobRoles.map((role) => role.ID));
    }
  }, [jobRoles]);

  const [clickedJobs, setClickedJobs] = useState([]);

  const {
    fetchNextPage,
    data,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading, // ✅ for first load only
  } = usePaginatedQuery({
    queryKey: ["user-management"],
    url: jobRoleList?.OUTPUT?.length > 0 ? "job/fetchClinicianJob" : null,
    enabled: true,
    parmeter: { ...values, JOB_STATUS: jobTabactive },
  });

  // ✅ Single refetch useEffect using debouncedJobRoles
  useEffect(() => {
    refetch();
  }, [debouncedSearch, debouncedJobRoles, jobTabactive]);

  const sortedJobs = data?.pages
    .flatMap((page) => page.OUTPUT)
    .filter((job, index, array) => array.findIndex(j => j.ID === job.ID) === index)
    .sort((a, b) => {
      const aIndex = clickedJobs.indexOf(a.ID);
      const bIndex = clickedJobs.indexOf(b.ID);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  const visibleJobs = sortedJobs?.filter((job) => {
    const status = job?.cliniciansInFacilityJobShifts[0]?.clinicianStatus;
    const offerStatus = job?.cliniciansInFacilityJobShifts[0]?.offerStatus;
    
    // For "Open Jobs" tab: Hide jobs that are Hired or have accepted offers
    if (jobTabactive === "Open Jobs") {
      const shouldHide =
        ["Accepted", "Hired", "OfferAccepted"].includes(status) ||
        offerStatus === "OfferAccepted";
      return !shouldHide;
    }
    
    // For "Assigned Jobs" tab: Show ONLY jobs that are Hired or have accepted offers
    if (jobTabactive === "Assigned Jobs") {
      const isAssigned =
        ["Hired", "OfferAccepted"].includes(status) ||
        offerStatus === "OfferAccepted";
      return isAssigned;
    }
    
    return true;
  });

  useEffect(() => {
    if (!customFetch) {
      if (inView && hasNextPage && !isFetchingNextPage) {
        if (data?.pageParams.length !== data.pages[0]?.meta?.totalPages) {
          fetchNextPage();
        }
      }
    } else {
      refetch();
      setCustomFetch(false);
    }
  }, [customFetch, inView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleCardClick = useCallback(
    (job) => {
      setIsDetailsOpen(false);
      dispatch(setSelectedCard(null));
      setSelectedJob(null);
      setClickedJobs((prev) => {
        const newClicked = prev.filter((id) => id !== job.ID);
        return [job.ID, ...newClicked];
      });
      setTimeout(() => {
        setSelectedJob(job);
        dispatch(setSelectedCard(job));
        setIsDetailsOpen(true);
      }, 0);
    },
    [dispatch],
  );

  const handleCloseDetails = useCallback(() => {
    setIsDetailsOpen(false);
    dispatch(setSelectedCard(null));
    setSelectedJob(null);
  }, [dispatch]);

  const handleSaveJob = useCallback(
    async (cardID) => {
      const response = await saveJobMutation.mutateAsync({
        USER_ID: userDetails?.ID,
        JOB_ID: cardID,
      });
      if (response.STATUS) {
        const updatedJob = {
          ...selectedCard,
          save_jobs: selectedCard?.save_jobs?.map((job, idx) =>
            idx === 0
              ? {
                  ...job,
                  SAVED: response?.OUTPUT?.SAVED == true ? true : false,
                }
              : job,
          ),
        };
        dispatch(
          updateSavedJob({ index: 0, saved: response?.OUTPUT?.SAVED == true }),
        );
        refetch();
        dispatch(setSelectedCard(updatedJob));
        setSelectedJob(updatedJob);
      }
    },
    [saveJobMutation, userDetails],
  );

  // ✅ Clear all resets to ALL roles
  const handleClearAllFilter = () => {
    setSearchValues("");
    setSelectedJobRoles(jobRoles.map((role) => role.ID));
    sessionStorage.clear();
  };

  useEffect(() => {
    if (TempID != null) {
      setSelectedJobRoles(jobRoles.map((role) => role.ID));
      refetch();
    }
  }, [TempID]);

  const handleJobRoleChange = (event) => {
    if (event.includes("All")) {
      if (selectedJobRoles.length === jobRoles.length) {
        setSelectedJobRoles([]);
      } else {
        setSelectedJobRoles(jobRoles.map((role) => role.ID));
      }
    } else {
      setSelectedJobRoles(event);
    }
  };

  let originalUrl = `${FRONTEND_URL}login?ref=${selectedJob?.ID}`;

  const handleCopyJobLink = () => {
    if (!originalUrl) {
      Swal.fire({
        icon: "error",
        title: "Link Not Found",
        text: "The link could not be copied because it is undefined.",
        showCloseButton: true,
      });
      return;
    }
    const onCopySuccess = () => {
      Swal.fire({
        icon: "success",
        title: "Link Copied!",
        showConfirmButton: false,
        timer: 1500,
        showCloseButton: true,
      });
      dispatch(setSelectedCard({ ...selectedCard, showModal1: false }));
    };
    const fallbackCopy = () => {
      copy(originalUrl);
      onCopySuccess();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(originalUrl)
        .then(onCopySuccess)
        .catch((err) => {
          console.error("Clipboard write failed:", err);
          showManualCopySwal(originalUrl);
        });
    } else {
      fallbackCopy();
    }
    setShowModal(false);
  };

  const showManualCopySwal = (url) => {
    Swal.fire({
      title: "Link Copied!",
      text: `If you're having trouble copying, please manually copy the link provided below:\n${url}`,
      icon: "info",
      confirmButtonText: "Copy the Link",
      customClass: {
        popup: "swal-popup",
        title: "swal-title",
        content: "swal-content",
      },
      showCloseButton: false,
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      copy(originalUrl);
    });
  };

  return (
    <>
      {/* Header */}
      <Grid
        container
        columnSpacing={2}
        px={2}
        py={4}
        lg={12}
        sx={{
          background: "white",
          flexWrap: "wrap",
          gap: "20px",
          marginLeft: "0px",
        }}
      >
        <Grid item lg={2.5} sx={{ alignItems: "center", display: "flex" }}>
          <TextField
            label="Search by title, specialty or facility"
            size="small"
            variant="outlined"
            value={searchValues}
            onChange={(e) => setSearchValues(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: "100%" }}
            InputProps={{
              style: { borderRadius: "100px" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Job Role Dropdown with Checkboxes */}
        <Grid item lg={2} sx={{ alignItems: "center", display: "flex", ml: 2 }}>
          <FormControl fullWidth>
            <InputLabel shrink>Filter by Job Role</InputLabel>
            <Select
              label="Filter by Job Role"
              multiple
              displayEmpty
              value={selectedJobRoles}
              onChange={(e) => handleJobRoleChange(e.target.value)}
              renderValue={(selected) =>
                selected.length === jobRoles.length
                  ? "All Roles Selected"
                  : jobRoles
                      .filter((role) => selected.includes(role.ID))
                      .map((role) => role.ROLE_NAME)
                      .join(", ")
              }
              size="small"
              sx={{ width: "100%" }}
            >
              <MenuItem value="All">
                <Checkbox
                  checked={selectedJobRoles.length === jobRoles.length}
                />
                <ListItemText primary="All" />
              </MenuItem>
              {jobRoles.map((role) => (
                <MenuItem key={role.ID} value={role.ID}>
                  <Checkbox checked={selectedJobRoles.includes(role.ID)} />
                  <ListItemText primary={role.ROLE_NAME} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid
          item
          lg={1}
          sx={{
            display: "flex",
            justifyContent: "center",
            borderLeft: "solid 1px black",
            ml: 1,
            pl: 1,
            pr: 1,
          }}
        >
          <Button
            sx={{
              color: "black",
              minWidth: "auto",
              px: 1,
              whiteSpace: "nowrap",
            }}
            onClick={handleClearAllFilter}
          >
            Clear All
          </Button>
        </Grid>
      </Grid>

      {/* ✅ Small inline loader instead of full screen overlay */}
      {isFetching && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {/* Jobs List and Details */}
      <Grid container mt={2}>
        <Grid
          item
          lg={isDetailsOpen ? 4 : 12}
          sx={{
            height: `calc(1040px - 240px)`,
            overflow: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#B7B7B7 transparent",
          }}
        >
          <>
            <Grid container spacing={1}>
              {jobsTypeTabs?.map((tab) => (
                <Grid item key={tab?.title}>
                  <Tabs
                    title={tab?.title}
                    active={tab?.title === jobTabactive}
                    onClick={() => {
                      setJobTabActive(tab?.title);
                      setIsDetailsOpen(false);
                    }}
                  />
                </Grid>
              ))}
              {TempID && (
                <Button
                  sx={{ color: "black", minWidth: "auto", px: 1 }}
                  onClick={handleClearAllFilter}
                >
                  Return to Jobs
                </Button>
              )}
            </Grid>

            {/* ✅ Smooth opacity fade + skeleton cards while loading */}
            <Grid
              container
              spacing={2}
              mt={1}
              sx={{
                opacity: isFetching ? 0.4 : 1,
                transition: "opacity 0.3s ease",
                pointerEvents: isFetching ? "none" : "auto",
              }}
            >
              {isFetching
                ? Array.from({ length: 6 }).map((_, i) => (
                    <JobCardSkeleton key={i} />
                  ))
                : visibleJobs?.map((data, index, array) => {
                    let isLastElement = index === array?.length - 1;
                    const isJobSaved = data?.save_jobs[0]?.SAVED || false;
                    const ClinicianStatus =
                      data?.cliniciansInFacilityJobShifts[0]?.clinicianStatus ||
                      "Not applied yet";
                    const InterviewDate = data?.cliniciansInFacilityJobShifts[0]
                      ?.interviewDateTime
                      ? formatDateAndTime(
                          data.cliniciansInFacilityJobShifts[0]
                            .interviewDateTime,
                        )
                      : "";
                    return (
                      <Grid
                        item
                        key={data.ID}
                        ref={isLastElement ? ref : null}
                        lg={isDetailsOpen ? 12 : 4}
                        marginRight={isDetailsOpen && 0.5}
                        className="clinician-jobs-cards"
                      >
                        <Card
                          sx={{
                            borderRadius: "8px",
                            marginBottom: 0.5,
                            height: "100%",
                            maxHeight: "260px",
                            padding: "8px",
                            boxShadow:
                              selectedJob?.ID === data.ID
                                ? "0 0 10px rgba(110, 190, 68, 0.6)"
                                : "none",
                            border:
                              selectedJob?.ID === data.ID
                                ? "1px solid #6ebe44"
                                : "1px solid #ddd",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "0 4px",
                            }}
                          >
                            <img
                              src={jobRoleImage}
                              alt="Job Role"
                              style={{ width: "40px", height: "40px" }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              <Tooltip title="Save Job">
                                <BookmarkBorderOutlinedIcon
                                  key={data.SAVED}
                                  style={{
                                    background: isJobSaved
                                      ? "linear-gradient(to right,#6ebe44,#4eb3e8)"
                                      : "",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSaveJob(data.ID);
                                  }}
                                />
                              </Tooltip>
                              <Tooltip
                                title={
                                  data?.HIRING_TYPE === "Self Hire"
                                    ? "Open Post"
                                    : "Sent by Admin"
                                }
                              >
                                <StarIcon
                                  style={{
                                    color:
                                      data?.HIRING_TYPE === "Self Hire"
                                        ? "gray"
                                        : "gold",
                                  }}
                                />
                              </Tooltip>
                            </Box>
                          </Box>

                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                              paddingX: "8px",
                              height: "30px",
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              overflow: "hidden",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                wordBreak: "break-word",
                                flex: 1,
                              }}
                            >
                              {data.specialty?.SPECIALTY_NAME}
                            </Typography>
                            <Tooltip title={InterviewDate || ""}>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  padding: "1px 6px",
                                  borderRadius: "4px",
                                  color: getStatusColor(
                                    data.cliniciansInFacilityJobShifts[0]
                                      ?.clinicianStatus,
                                  ),
                                  backgroundColor: getStatusBgColor(
                                    data.cliniciansInFacilityJobShifts[0]
                                      ?.clinicianStatus,
                                  ),
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getStatusonJobCard(ClinicianStatus)}
                              </Typography>
                            </Tooltip>
                          </Stack>

                          <Typography
                            sx={{
                              fontSize: "12px",
                              marginTop: 0.1,
                              paddingX: "8px",
                            }}
                            px={0.1}
                          >
                            {data?.user?.FACILITY_NAME} •{" "}
                            <span>
                              {data?.user?.CITY}, {data?.user?.STATE}
                            </span>
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "6px",
                              paddingX: "8px",
                            }}
                          >
                            <WorkOutlineOutlinedIcon
                              sx={{ fontSize: "14px" }}
                            />
                            <Typography
                              sx={{ fontSize: "12px", marginLeft: 0.8 }}
                            >
                              {data.JOB_TYPE}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "6px",
                              paddingX: "8px",
                            }}
                          >
                            <MonetizationOnOutlinedIcon
                              sx={{ fontSize: "14px" }}
                            />
                            <Typography
                              sx={{ fontSize: "12px", marginLeft: 0.8 }}
                            >
                              {`$${data.PAY_AMOUNT_FROM}-$${data.PAY_AMOUNT_TO}/hr`}
                            </Typography>
                          </Box>

                          <Typography
                            mt={0.5}
                            sx={{
                              fontSize: "11px",
                              cursor: "pointer",
                              paddingX: "8px",
                            }}
                          >
                            <span
                              className="loraFont"
                              onClick={() => handleCardClick(data)}
                            >
                              Start date{" "}
                              {data.SERVICE_DATE
                                ? `${new Date(data.SERVICE_DATE).getDate()} ${new Date(data.SERVICE_DATE).toLocaleString("default", { month: "short" })} ${new Date(data.SERVICE_DATE).getFullYear()}`
                                : "Latest"}
                            </span>
                          </Typography>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "left",
                              padding: "0px 8px",
                            }}
                          >
                            <Button
                              className="montserratFont"
                              sx={{
                                color: "rgba(0,0,0,0.7)",
                                fontSize: "11px",
                                border: "1px solid rgba(0,0,0,0.3)",
                                justifyContent: "center",
                                fontWeight: 600,
                                marginTop: 1,
                              }}
                            >
                              {data.jobs_role?.ROLE_NAME}
                            </Button>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 1,
                              paddingX: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "11px",
                                color: "#3BB9E3",
                                cursor: "pointer",
                              }}
                            >
                              <span className="loraFont">
                                Posted on{" "}
                                {data.CREATED_DATE
                                  ? `${new Date(data.CREATED_DATE).getDate()} ${new Date(data.CREATED_DATE).toLocaleString("default", { month: "short" })} ${new Date(data.CREATED_DATE).getFullYear()}`
                                  : "Latest"}
                              </span>
                            </Typography>

                            <Tooltip title="View Job">
                              <Typography
                                sx={{
                                  color: "#02230C",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  border: "1px solid #02230C",
                                  padding: "3px 10px",
                                  borderRadius: "4px",
                                }}
                                onClick={() => handleCardClick(data)}
                              >
                                <span className="loraFont">View Job</span>
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
            </Grid>
          </>
        </Grid>

        {isDetailsOpen && selectedJob && (
          <Grid item lg={8}>
            <Button onClick={handleCloseDetails}>Close</Button>
            <JobCardDetails
              data={selectedJob}
              onSaved={() => {
                dispatch(setSelectedCard(selectedCard));
                setTimeout(() => {
                  refetch();
                }, 10);
                handleCardClick(selectedJob);
              }}
            />
          </Grid>
        )}
      </Grid>

      {showModal && (
        <Dialog
          open={showModal}
          onClose={() =>
            dispatch(setSelectedCard({ ...selectedCard, showModal1: false }))
          }
          fullWidth
          maxWidth="sm"
        >
          <Card
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflowY: "auto",
              maxHeight: "70vh",
              p: { xs: 2, sm: 3 },
            }}
          >
            <CardContent sx={{ width: "100%" }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#000",
                    opacity: 0.87,
                  }}
                >
                  Share Job
                </Typography>
                <IconButton
                  onClick={() =>
                    dispatch(
                      setSelectedCard({ ...selectedCard, showModal1: false }),
                    )
                  }
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: "14px", marginLeft: 0.1 }} mb={5}>
                Easily Share This Job with Registered Clinicians
              </Typography>
              <TextField
                variant="outlined"
                InputProps={{ readOnly: true }}
                value={originalUrl}
                disabled
                sx={{ mb: 2, width: "100%" }}
              />
              <Box display="flex" justifyContent="flex-end">
                <Button
                  id="Copy"
                  onClick={handleCopyJobLink}
                  variant="contained"
                  className="custombuttons"
                >
                  🔗 Copy Link
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Dialog>
      )}
    </>
  );
};

export default ClinicianJobs;
